import { EmptyState, StatusChip } from '../../components/index.js';

function visibleActions(identity) {
  if (!identity) return [];
  if (identity.status === 'pending') {
    return ['approve', 'deny', 'grantApprover'];
  }
  if (identity.status === 'approved') {
    return ['suspend', 'revoke', 'grantApprover', 'removeApprover'];
  }
  if (identity.status === 'suspended') {
    return ['restore', 'revoke'];
  }
  return [];
}

function actionLabel(action) {
  switch (action) {
    case 'approve':
      return 'Approve access';
    case 'deny':
      return 'Deny';
    case 'suspend':
      return 'Suspend';
    case 'restore':
      return 'Restore';
    case 'revoke':
      return 'Revoke';
    case 'grantApprover':
      return 'Make approver';
    case 'removeApprover':
      return 'Remove approver';
    default:
      return action;
  }
}

function actionTone(action) {
  return ['deny', 'suspend', 'revoke', 'removeApprover'].includes(action) ? 'ghost intent-danger' : 'secondary';
}

export function SiteAccessDecisionPanel({
  identity,
  noteDraft,
  onNoteChange,
  onAction,
  mutationDisabled = false,
  isOwnerViewer = false,
  isDelegateViewer = false,
}) {
  if (!identity) {
    return (
      <section className="card compact site-access-decision" data-testid="decision-panel" aria-live="polite">
        <div className="site-access-admin__section-head">
          <h2>Decision</h2>
        </div>
        <div data-testid="decision-empty">
          <EmptyState
            title="No identity selected"
            description="Choose an EDIPN from the queue to review the certificate snapshot, justification, and available actions."
          />
        </div>
      </section>
    );
  }

  const actions = visibleActions(identity);
  const ownerProtected = Boolean(identity.isOwner);
  const disableMutations = mutationDisabled || (ownerProtected && !isOwnerViewer) || ownerProtected;

  return (
    <section className="card compact site-access-decision" data-testid="decision-panel" aria-live="polite">
      <div className="site-access-admin__section-head">
        <div>
          <h2>Decision</h2>
          <div className="site-access-admin__assistive-copy">
            Review the certificate snapshot, justification, and next action.
          </div>
        </div>
      </div>

      <div className="site-access-decision__identity">
        <div>
          <div className="site-access-decision__name" data-testid="decision-name">
            {identity.fullName || 'Unknown CAC holder'}
          </div>
          <div className="mono site-access-decision__edipn" data-testid="decision-edipn">
            {identity.edipn}
          </div>
        </div>
        <StatusChip status={identity.status}>
          <span data-testid="decision-status">{identity.status}</span>
        </StatusChip>
      </div>

      <div className="site-access-decision__grid">
        <div className="site-access-decision__field">
          <div className="eyebrow">Organization</div>
          <div data-testid="decision-org">{identity.organization || 'No organization provided'}</div>
        </div>
        <div className="site-access-decision__field">
          <div className="eyebrow">Last reviewed</div>
          <div>{identity.reviewedAt ? new Date(identity.reviewedAt).toLocaleString() : 'Not reviewed yet'}</div>
        </div>
        <div
          className="site-access-decision__field site-access-decision__field-wide"
          data-testid="decision-cert-snapshot"
        >
          <div className="eyebrow">Certificate audit snapshot</div>
          <div className="mono site-access-decision__cert-value">
            {identity.certSubject || 'No certificate subject captured.'}
            {identity.certIssuer ? ` · Issuer: ${identity.certIssuer}` : ''}
          </div>
        </div>
        <div className="site-access-decision__field site-access-decision__field-wide">
          <div className="eyebrow">Justification</div>
          <div data-testid="decision-justification">
            {identity.justification || 'No requester justification was provided.'}
          </div>
        </div>
        <div className="site-access-decision__field site-access-decision__field-wide">
          <div className="eyebrow">Last decision note</div>
          <div data-testid="decision-note-display">
            {identity.decisionNote || 'No decision note recorded yet.'}
          </div>
        </div>
      </div>

      <label className="form-label" htmlFor="site-access-decision-note">
        Decision note
      </label>
      <textarea
        id="site-access-decision-note"
        className="form-input form-textarea"
        data-testid="decision-note-input"
        value={noteDraft}
        onChange={(event) => onNoteChange(event.target.value)}
        placeholder="Capture the basis for this site-access decision…"
      />

      {ownerProtected ? (
        <div className="site-access-decision__owner-lock" data-testid="owner-locked-notice">
          <strong>The root owner is pinned in configuration and cannot be denied, suspended, revoked, or removed as approver.</strong>
        </div>
      ) : null}

      <div className="site-access-decision__actions">
        {actions.map((action) => (
          <button
            key={action}
            type="button"
            className={`btn ${actionTone(action)}`}
            data-testid={`action-${action.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)}`}
            disabled={disableMutations}
            onClick={() => onAction(action)}
            title={
              mutationDisabled
                ? 'The access directory is temporarily unavailable — changes are paused.'
                : ownerProtected && isDelegateViewer
                  ? 'Delegated approvers cannot change the pinned owner.'
                  : ownerProtected
                    ? 'The pinned owner cannot be changed.'
                    : undefined
            }
          >
            {actionLabel(action)}
          </button>
        ))}
      </div>
    </section>
  );
}
